import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../../utils/apolloClient';
import config from '../../../utils/config';

const initialState = {
  selectedLocation: '',
  editFieldSelection: {},
  apiFieldsMap: {},
  apiRequiredFieldsMap: {},
  selectedLocationName: '',
  apiFieldSearchInput: '',
  selectedApiDetails: {},
  fieldDetail: {},
  allFieldsUncheckedApiList: [],
  currentFieldSelection: {},
  updatedFieldSelection: {},
  saveComplete: false,
  fetchingSubscriptionFields: false,
  failedToFetchSubscriptionFields : false,
  savingSubscriptionFields: false,
  failedToSaveSubscriptionFields : false,
  prevLocation: '',
  fieldSearchInput: '',
  isvFields: {},
  isvRequiredFields: {},
};

export const getSubscriptionFields = createAsyncThunk(
  'getSubscriptionFields',
  async ({ solutionId, entityId, token, isBulkSubscribed }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/api-fields`,
      params: {
        solutionId,
        entityId,
        isBulkSubscribed: isBulkSubscribed ? 'true' : 'false',
      },
      headers:{
        Authorization : `Bearer ${token}`
      }
    });
    return response;
  }
);

export const updateSubscriptionFields = createAsyncThunk(
  'updateSubscriptionFields',
  async ({ 
    solutionId, 
    apiFieldSelections, 
    solutionData,
    subscriptionDetails,
    updatedAPIList,
    token,
    isBulkSubscribed = false
   }) => {
    let response = await client.authorizeRequest({
      method: 'PUT',
      url: `${config.serviceEndpoints.mpDpMetadataUrl}/edit-api-fields/solutions/${solutionId}`,
      data: {
        apiFieldSelections,
        solutionData,
        subscriptionDetails,
        updatedAPIList,
        isBulkSubscribed
      },
      headers:{
        Authorization : `Bearer ${token}`
      }
    });
    return response;
  }
);


export const apiFieldsSlice = createSlice({
  name: 'apiFieldsSlice',
  initialState,
  reducers: {
    setSelectedRequiredField: (state, action) => {
      state.fieldDetail = action.payload;
    },
    removeSelectedRequiredfield: (state, action) => {
      state.fieldDetail = {};
    },
    initialiseAPIFields: (state, action) => {
      const { mapApiNamesAndFields } = action.payload;
      const apiData = {};
      const apiFieldsData = {};
      const apiRequiredFieldsData = {};
      mapApiNamesAndFields?.forEach((f) => {
        const apiId = f?.apiId;

        const manualSelectionFieldsSet = Array.isArray(f?.manualSelectionFields) &&
            f?.manualSelectionFields.length > 0 
            ? new Set(f.manualSelectionFields) 
            : null;

        let initialFieldsSelection = [];
        let allApiFields = [];
        let apiRequiredFields = [];
        f?.fields.forEach((field) => {
          allApiFields.push(field.name);
          // Only include field if it's not in manualSelectionFields
          if (!manualSelectionFieldsSet?.has(field.name)) {
            initialFieldsSelection.push(field.name);
          }
          if (field.required) {
            apiRequiredFields.push(field.name);
          }
        });
        
        apiData[apiId] = initialFieldsSelection;
        apiFieldsData[apiId] = allApiFields;
        apiRequiredFieldsData[apiId] = apiRequiredFields;
      });

      if(JSON.stringify(state.editFieldSelection) === '{}'){
        state.editFieldSelection = apiData;
        state.apiFieldsMap = apiFieldsData;
        state.apiRequiredFieldsMap = apiRequiredFieldsData;
      }
    },
    setSelectedLocation: (state, action) => {
      state.selectedLocation = action?.payload?.entityId;
      state.selectedLocationName = action?.payload?.location;
    },
    setSelectedApi: (state, action) => {
      state.selectedApiDetails = action.payload;
    },
    setApiFieldSearchInput: (state, action) => {
      state.apiFieldSearchInput = action.payload;
    },
    updateSelectedFields: (state, action) => {
      const { operation, field, selectedApiId } = action.payload;
      const copy = {...state.updatedFieldSelection}
      let apiFields;
      if (operation === 'addField') {
        if(copy[selectedApiId]){
        apiFields = [...copy[selectedApiId]];
        apiFields.push(field);
        }
        else{
          apiFields = [field]
        }

        state.updatedFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
      }
      if (operation === 'addAllFields') {
        apiFields = field;
        state.updatedFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
      }
      if (operation === 'removeField') {
        const apiFields = [...copy[selectedApiId]];
        const index = apiFields.indexOf(field);
        if (index !== -1) {
          apiFields.splice(index, 1);
        }
        state.updatedFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
      }
      if (operation === 'removeAllFields') {
        const apiFields = field;
        state.updatedFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
      }
    },
    updateInitialSelectedFields: (state, action) => {
      const { operation, field, selectedApiId, apiName } = action.payload;
      const copy = {...state.editFieldSelection}
      const apiList = state.allFieldsUncheckedApiList;
      let apiFields;
      if (operation === 'enableField') {
        if(copy[selectedApiId]){
        apiFields = [...copy[selectedApiId]];
        apiFields.push(field);
        } else{
          apiFields = [field]
        }
        if(apiFields.length !== 0 && apiList.includes(apiName)) {
          let newApiList = apiList.filter(api => api != apiName);
          state.allFieldsUncheckedApiList = newApiList;
        }
        state.editFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
      }
      if (operation === 'enableAllFields') {
        apiFields = field;
        state.editFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
        if (apiFields.length !== 0 && apiList.includes(apiName)) {
          let newApiList = apiList.filter(api => api != apiName);
          state.allFieldsUncheckedApiList = newApiList;
        }
      }
      if (operation === 'disableField') {
        const apiFields = [...copy[selectedApiId]];
        const index = apiFields.indexOf(field);
        if (index !== -1) {
          apiFields.splice(index, 1);
        }
        if(apiFields.length === 0 && !apiList.includes(apiName)) {
          apiList.push(apiName);
          state.allFieldsUncheckedApiList = apiList;
        }
        state.editFieldSelection = {
          ...copy,
          [selectedApiId]: apiFields,
        };
      }
      if (operation === 'disableAllFields') {
        const apiFields = field;
        if (apiFields.length === 0 && !apiList.includes(apiName)) {
          apiList.push(apiName);
          state.allFieldsUncheckedApiList = apiList;
        }
        state.editFieldSelection = {
          ...copy,
          [selectedApiId]: field,
        };
      }
    },
    updateSaveComplete: (state, action) =>{
      state.saveComplete =  action.payload.status
    },
    setISVFields: (state, action) => {
      state.isvFields = action.payload;
    },
    setFieldSearchInput: (state, action) => {
      state.fieldSearchInput = action.payload;
    },
    setISVRequiredFields: (state, action) => {
      state.isvRequiredFields = action.payload;
    },
    clearState: (state) => {
      state.saveComplete = false;
      state.selectedLocation = ""
      state.selectedLocationName = ""
      state.failedToFetchSubscriptionFields = false;
      state.failedToSaveSubscriptionFields = false;
      state.currentFieldSelection = {}
      state.updatedFieldSelection = {}
      state.prevLocation = "";
      state.isvFields = {};
      state.isvRequiredFields = {};
      state.selectedApiDetails = {};
      state.fieldSearchInput = '';
    }
  },
  extraReducers: {
    [getSubscriptionFields.fulfilled]: (state, action) => {
      const fields = action?.payload?.data[0]?.apiFieldSelections
      state.currentFieldSelection = {...fields}
      state.updatedFieldSelection = {...fields}
      state.fetchingSubscriptionFields = false;
      state.failedToFetchSubscriptionFields = false;
    },
    [getSubscriptionFields.pending]: state => {
      state.fetchingSubscriptionFields = true;
      state.failedToFetchSubscriptionFields = false;
      state.prevLocation = state.selectedLocation;
      state.selectedApiDetails = {};
      state.fieldSearchInput = '';
    },
    [getSubscriptionFields.rejected]: state => {
      state.fetchingSubscriptionFields = false;
      state.failedToFetchSubscriptionFields = true;
    },
    [updateSubscriptionFields.fulfilled]: (state, action) => {
      state.savingSubscriptionFields = false;
      state.failedToSaveSubscriptionFields = false;
      state.saveComplete = true;
      state.prevLocation = "";
    },
    [updateSubscriptionFields.pending]: state => {
      state.savingSubscriptionFields = true;
      state.failedToSaveSubscriptionFields = false;
    },
    [updateSubscriptionFields.rejected]: state => {
      state.savingSubscriptionFields = false;
      state.failedToSaveSubscriptionFields = true;
    }
  }
});

export const {
  setSelectedRequiredField,
  removeSelectedRequiredfield,
  initialiseAPIFields,
  setSelectedApi,
  setApiFieldSearchInput,
  setSelectedLocation,
  updateSelectedFields,
  updateInitialSelectedFields,
  updateSaveComplete,
  setISVFields,
  setFieldSearchInput,
  setISVRequiredFields,
  clearState,
} = apiFieldsSlice.actions;

export default apiFieldsSlice.reducer;
